import { useQuery } from "@tanstack/react-query";
import {
  getAvailableDaysByMonth,
  getTeacherSlotByDayOnly,
} from "api/teacher.api.ts";
import {
  bothGroups,
  checkTeacherSlotTypes,
  defaultNumberOfStudents,
  defaultTypeClasses,
  group,
  privateGroup,
} from "conts";
import moment from "moment";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useMediaQuery } from "react-responsive";

const typeOfPossibleClasses = [
  { name: privateGroup, value: [defaultTypeClasses.Private] },
  { name: group, value: [defaultTypeClasses.Group] },
  {
    name: bothGroups,
    value: [defaultTypeClasses.Private, defaultTypeClasses.Group],
  },
];
const convertClassTypeValueToLabel = (value) =>
  value === defaultTypeClasses.Private.value
    ? typeOfPossibleClasses[0].name
    : typeOfPossibleClasses[1].name;

export const getNumberOfStudentsOptions = (teacherInfo, typeOfClass) => {
  const maxStudents =
    typeOfClass?.value === "Group"
      ? teacherInfo?.number_of_students
      : teacherInfo?.number_of_students_private_class;

  if (!maxStudents) return [];

  return Array.from({ length: maxStudents }, (_, index) => ({
    label: `${index + 1} student${index + 1 > 1 ? "s" : ""}`,
    value: index + 1,
  }));
};

const initialState = {
  selectedDate: new Date(),
  typeOfClass: defaultTypeClasses.Private,
  numberOfStudents: defaultNumberOfStudents,
  availableGroupTypes: [defaultTypeClasses.Private],
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_SELECTED_DATE":
      return { ...state, selectedDate: action.payload };
    case "SET_TYPE_OF_CLASS":
      return {
        ...state,
        typeOfClass: action.payload,
        numberOfStudents: initialState.numberOfStudents,
      };
    case "SET_NUMBER_OF_STUDENTS":
      return { ...state, numberOfStudents: action.payload };
    case "SET_GROUP_TYPE_OPTIONS":
      return { ...state, availableGroupTypes: action.payload };
    default:
      return state;
  }
};

export const useTeacherSlots = ({
  teacherInfo,
  online_type,
  booking_time,
  booking_date,
}) => {
  const [
    { selectedDate, typeOfClass, numberOfStudents, availableGroupTypes },
    dispatch,
  ] = useReducer(reducer, initialState);

  const [availableDaysMap, setAvailableDaysMap] = useState({});

  const [isDatesLoading, setDatesLoading] = useState(false);

  const { data: availableDates, isLoading: availableDaysLoading } = useQuery({
    queryKey: [
      "getAvailableDaysByMonth",
      teacherInfo?.id,
      typeOfClass,
      numberOfStudents,
      availableGroupTypes,
      online_type,
      booking_time,
      booking_date,
    ],
    queryFn: () =>
      getAvailableDaysByMonth({
        online_type,
        teacher_id: teacherInfo?.id,
        month: moment().format("YYYY-MM"),
        type:
          online_type === "online"
            ? "private"
            : typeOfClass?.value?.toLowerCase(),
        number_of_participants: numberOfStudents?.value,
        booking_time,
        booking_date,
      }),
    enabled: !!teacherInfo?.id,
  });

  useEffect(() => {
    if (!availableDates) return;

    const firstAvailableDateString = Object.keys(availableDates).find(
      (date) => availableDates[date].has_available_slots === true
    );

    if (!firstAvailableDateString) return;

    const date = moment(firstAvailableDateString).toDate();
    const month = moment(firstAvailableDateString).format("YYYY-MM");

    setAvailableDaysMap((prev) => ({
      ...prev,
      [month]: availableDates,
    }));

    dispatch({ type: "SET_SELECTED_DATE", payload: date });
  }, [availableDates]);

  useEffect(() => {
    const { has_group, has_private } = checkTeacherSlotTypes(
      teacherInfo?.slots
    );

    const typeMap = {
      true_true: typeOfPossibleClasses[2].value, // both group
      true_false: typeOfPossibleClasses[0].value, // private classes
      false_true: typeOfPossibleClasses[1].value, // group classes
      false_false: [defaultTypeClasses.Private], // default
    };

    const possibleTypeOfClasses = typeMap[`${has_private}_${has_group}`];

    dispatch({ type: "SET_TYPE_OF_CLASS", payload: possibleTypeOfClasses[0] });

    dispatch({
      type: "SET_GROUP_TYPE_OPTIONS",
      payload: possibleTypeOfClasses,
    });
  }, [teacherInfo?.slots]);

  const { data: teacherSlots, isLoading } = useQuery({
    queryKey: [
      "getTeacherSlotByDayOnly",
      teacherInfo?.id,
      selectedDate,
      online_type,
    ],
    queryFn: () =>
      getTeacherSlotByDayOnly({
        teacher_id: teacherInfo?.id,
        day: moment(selectedDate).format("dddd"),
        date: moment(selectedDate).format("YYYY-MM-DD"),
        online_type,
      }),
    enabled: !!teacherInfo?.id,
  });

  const formattedDate = moment(selectedDate).format("MMMM DD, YYYY");

  const filteredTimings = useMemo(() => {
    const now = moment(
      new Date().toLocaleString("en-US", {
        timeZone: teacherInfo?.timezone || "UTC",
      })
    );
    return (
      teacherSlots?.data?.teacher_class_timings?.filter((timing) => {
        const type = online_type === "online" ? "Private" : typeOfClass?.value;
        // Filter by class type
        if (timing.type !== type) return false;

        // Check if class is in the past
        const classTime = moment(
          `${formattedDate} ${timing.time}`,
          "MMMM DD, YYYY hh:mm A"
        );
        if (classTime.isBefore(now)) return false;

        // Check if class starts - 60 minutes
        if (classTime.isBefore(now.clone().add(60, "minutes"))) return false;

        // Private
        if (
          timing.type === "Private" &&
          (timing.booked || timing.timeSlotStatus === 2)
        ) {
          if (timing.timeSlotStatus !== 3) return false;
        }

        // Group
        if (timing.type === "Group") {
          const remainingSlots =
            timing.totalBookingSlots - timing.totalConfirmedBookingSlots;
          if (remainingSlots < numberOfStudents) return false;
        }

        return true;
      }) || []
    );
  }, [
    teacherSlots?.data?.teacher_class_timings,
    typeOfClass?.value,
    formattedDate,
    numberOfStudents,
    availableDates,
  ]);

  const availableTimings = useMemo(() => {
    return (
      filteredTimings.map((timing) => ({
        id: timing.id,
        date: formattedDate,
        time: timing?.time,
        type: online_type === "online" ? "online" : timing.type,
        cost: `$${
          online_type === "online"
            ? teacherInfo?.price_per_online_class
            : timing.type === "Private"
            ? teacherInfo?.price_per_private_class
            : teacherInfo?.price_per_group_class
        }`,
        remainingSlots:
          timing.type === "Group"
            ? timing.totalBookingSlots - timing.totalConfirmedBookingSlots
            : null,
      })) || []
    );
  }, [filteredTimings, formattedDate, teacherInfo, availableDates]);

  const sortedAvailableTimings = useMemo(() => {
    return availableTimings.sort((a, b) => {
      return moment(a.time, "hh:mm A").diff(moment(b.time, "hh:mm A"));
    });
  }, [availableTimings]);

  const handleMonthChange = async (date) => {
    try {
      const maxAllowedDate = moment().add(2, "months");
      const selectedDate = moment(date);

      if (selectedDate.isAfter(maxAllowedDate, "month")) {
        console.warn("Cannot select a month more than 2 months from today");
        return;
      }

      setDatesLoading(true);
      const month = moment(date).format("YYYY-MM");

      const res = await getAvailableDaysByMonth({
        online_type,
        teacher_id: teacherInfo?.id,
        month: month,
        type:
          online_type === "online"
            ? "private"
            : typeOfClass?.value.toLowerCase(),
        number_of_participants: numberOfStudents?.value,
        booking_time,
        booking_date,
      });

      setAvailableDaysMap((prev) => ({
        ...prev,
        [month]: res,
      }));
    } catch (error) {
      console.error("Error fetching available days", error);
    } finally {
      setDatesLoading(false);
    }
  };

  const slotsFilterDate = useCallback(
    (date) => {
      const formattedDate = moment(date).format("YYYY-MM-DD");
      const monthKey = moment(date).format("YYYY-MM");

      const monthAvailability = availableDaysMap[monthKey];

      return !!monthAvailability?.[formattedDate]?.has_available_slots;
    },
    [availableDaysMap]
  );

  const setTypeOfClass = (type) => {
    dispatch({ type: "SET_TYPE_OF_CLASS", payload: type });
  };

  return {
    selectedDate,
    setSelectedDate: (date) =>
      dispatch({ type: "SET_SELECTED_DATE", payload: date }),
    typeOfClass,
    setTypeOfClass,
    numberOfStudents,
    setNumberOfStudents: (number) =>
      dispatch({ type: "SET_NUMBER_OF_STUDENTS", payload: number }),
    availableTimings: sortedAvailableTimings,
    slotsFilterDate,
    availableGroupTypes,
    isLoading,
    daysLoading: availableDaysLoading || isDatesLoading,
    handleMonthChange,
  };
};
